import { FC, lazy, Suspense } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import { useSelector } from 'react-redux';

import Sold from 'dibs-icons/exports/Sold';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';

const PdpSharedContactSellerButton = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpSharedContactSellerButton" */
            '../../PdpSharedContactSellerButton/PdpSharedContactSellerButton'
        )
);

import { PdpRespTopModule_item$key } from './__generated__/PdpRespTopModule_item.graphql';
import { PdpRespTopModule_user$key } from './__generated__/PdpRespTopModule_user.graphql';
import { PdpRespTopModule_viewer$key } from './__generated__/PdpRespTopModule_viewer.graphql';

import styles from './PdpRespTopModule.scss';

const viewerFragment = graphql`
    fragment PdpRespTopModule_viewer on Viewer {
        ...PdpSharedContactSellerButton_viewer
    }
`;

const userFragment = graphql`
    fragment PdpRespTopModule_user on User {
        ...PdpSharedContactSellerButton_user
    }
`;

const itemFragment = graphql`
    fragment PdpRespTopModule_item on Item {
        ...PdpSharedContactSellerButton_item
        title
        photos {
            smallPath
        }
    }
`;

// TODO: replace with State type
type ReduxState = {
    relayVariables: {
        variables: {
            isClient: boolean;
        };
    };
};

export const PdpRespTopModule: FC<{
    item: PdpRespTopModule_item$key;
    user: PdpRespTopModule_user$key;
    viewer: PdpRespTopModule_viewer$key;
}> = ({ ...props }) => {
    const viewer = useFragment(viewerFragment, props.viewer);
    const user = useFragment(userFragment, props.user);
    const item = useFragment(itemFragment, props.item);

    const { isClient } = useSelector((state: ReduxState) => state.relayVariables.variables);

    return (
        <HeadingLevel>
            {Heading => (
                <div className={styles.container}>
                    <div className={styles.imageContainer}>
                        <img
                            src={item.photos?.[0]?.smallPath || ''}
                            alt={item.title || ''}
                            className={styles.image}
                        />
                    </div>

                    <div className={styles.content}>
                        <Sold className={styles.soldIcon} />
                        <Heading className={styles.title}>{item.title}</Heading>
                    </div>

                    <div className={styles.buttonContainer}>
                        {isClient && (
                            <Suspense fallback={null}>
                                <PdpSharedContactSellerButton
                                    type="transparent"
                                    size="medium"
                                    launchOrigin="contact link in sold pdp - topnav"
                                    viewer={viewer}
                                    user={user}
                                    item={item}
                                >
                                    <FormattedMessage
                                        id="PdpRespTopModule.messageTheSeller"
                                        defaultMessage="Message the Seller"
                                    />
                                </PdpSharedContactSellerButton>
                            </Suspense>
                        )}
                    </div>
                </div>
            )}
        </HeadingLevel>
    );
};
